import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  Settings, Plus, Trash2, GripVertical, Eye, EyeOff, 
  RefreshCw, ClipboardList, FileText, FileEdit,
  ShieldAlert, RotateCcw, ChevronDown, ChevronRight,
  Users, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';

const FORM_TYPES = [
  { id: 'assessment_ot', name: 'OT Assessment', icon: ClipboardList, color: 'blue' },
  { id: 'assessment_slt', name: 'SLT Assessment', icon: ClipboardList, color: 'purple' },
  { id: 'evaluation_ot', name: 'OT Evaluation', icon: FileText, color: 'teal' },
  { id: 'evaluation_slt', name: 'SLT Evaluation', icon: FileText, color: 'pink' },
  { id: 'soap_note', name: 'SOAP Note', icon: FileEdit, color: 'amber' },
];

const FIELD_TYPES = [
  { value: 'textarea', label: 'Long Text (Textarea)' },
  { value: 'text', label: 'Short Text (Input)' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Dropdown' },
];

export default function FormConfigPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState({});
  const [activeForm, setActiveForm] = useState('assessment_ot');
  const [canManage, setCanManage] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  // Dialog states
  const [showAddSectionDialog, setShowAddSectionDialog] = useState(false);
  const [showAddFieldDialog, setShowAddFieldDialog] = useState(false);
  const [showAccessDialog, setShowAccessDialog] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Form states
  const [newSection, setNewSection] = useState({ title: '', required: false });
  const [newField, setNewField] = useState({ label: '', type: 'textarea', required: false });
  
  // Access management
  const [users, setUsers] = useState([]);
  const [accessGrants, setAccessGrants] = useState([]);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const [configsRes, canManageRes] = await Promise.all([
        api.get('/form-config'),
        api.get('/form-config/can-manage')
      ]);
      setConfigs(configsRes.data);
      setCanManage(canManageRes.data.can_manage);
      setIsSuperAdmin(canManageRes.data.is_super_admin);
    } catch (error) {
      console.error('Error fetching form configs:', error);
      toast.error('Failed to load form configurations');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchUsers = useCallback(async () => {
    try {
      const [usersRes, grantsRes] = await Promise.all([
        api.get('/staff'),
        api.get('/auth/access-grants')
      ]);
      setUsers(usersRes.data || []);
      // Filter grants for form_config resource type
      setAccessGrants((grantsRes.data || []).filter(g => g.resource_type === 'form_config'));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
    }
  }, [isSuperAdmin, fetchUsers]);

  const toggleSectionExpanded = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Section handlers
  const handleToggleSection = async (formType, sectionId) => {
    try {
      await api.put(`/form-config/${formType}/sections/${sectionId}/toggle`);
      toast.success('Section toggled');
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle section');
    }
  };

  const handleAddSection = async () => {
    if (!newSection.title.trim()) {
      toast.error('Please enter a section title');
      return;
    }
    try {
      await api.post(`/form-config/${activeForm}/sections`, newSection);
      toast.success('Section added successfully');
      setShowAddSectionDialog(false);
      setNewSection({ title: '', required: false });
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add section');
    }
  };

  const handleRemoveSection = async (formType, sectionId, sectionTitle) => {
    if (!confirm(`Are you sure you want to remove "${sectionTitle}"?`)) return;
    try {
      await api.delete(`/form-config/${formType}/sections/${sectionId}`);
      toast.success('Section removed');
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove section');
    }
  };

  // Field handlers
  const handleAddField = async () => {
    if (!newField.label.trim() || !selectedSection) {
      toast.error('Please enter a field label');
      return;
    }
    try {
      await api.post(`/form-config/${activeForm}/sections/${selectedSection}/fields`, newField);
      toast.success('Field added successfully');
      setShowAddFieldDialog(false);
      setNewField({ label: '', type: 'textarea', required: false });
      setSelectedSection(null);
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add field');
    }
  };

  const handleToggleField = async (formType, sectionId, fieldId) => {
    try {
      await api.put(`/form-config/${formType}/sections/${sectionId}/fields/${fieldId}/toggle`);
      toast.success('Field toggled');
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle field');
    }
  };

  const handleRemoveField = async (formType, sectionId, fieldId, fieldLabel) => {
    if (!confirm(`Are you sure you want to remove "${fieldLabel}"?`)) return;
    try {
      await api.delete(`/form-config/${formType}/sections/${sectionId}/fields/${fieldId}`);
      toast.success('Field removed');
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove field');
    }
  };

  // Reset handler
  const handleResetForm = async (formType) => {
    if (!confirm('Reset to default settings? All customizations will be lost.')) return;
    try {
      await api.post(`/form-config/${formType}/reset`);
      toast.success('Form reset to defaults');
      fetchConfigs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset form');
    }
  };

  // Access management handlers
  const handleGrantAccess = async (userId, userEmail) => {
    try {
      await api.post('/auth/access-grants', {
        user_id: userId,
        user_email: userEmail,
        resource_type: 'form_config',
        can_view: true,
        can_edit: true,
        reason: 'Form configuration access granted by Super Admin'
      });
      toast.success('Access granted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    }
  };

  const handleRevokeAccess = async (grantId) => {
    try {
      await api.delete(`/auth/access-grants/${grantId}`);
      toast.success('Access revoked');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke access');
    }
  };

  const currentConfig = configs[activeForm] || { sections: [] };
  const sections = currentConfig.sections || [];

  if (!canManage && !loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="form-config-denied">
        <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mb-6">
          <ShieldAlert className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
        <p className="text-slate-500 text-center max-w-md">
          You don't have permission to manage form configurations. Contact the Super Admin to request access.
        </p>
        <Button onClick={() => window.history.back()} className="mt-6">Go Back</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="form-config-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-3">
            <Settings className="w-8 h-8" />
            Form Configuration
          </h1>
          <p className="text-slate-500 mt-1">
            Customize sections and fields for clinical forms
          </p>
        </div>
        {isSuperAdmin && (
          <Button onClick={() => setShowAccessDialog(true)} variant="outline">
            <Users className="w-4 h-4 mr-2" />
            Manage Access
          </Button>
        )}
      </div>

      {/* Info Alert */}
      <Alert className="bg-blue-50 border-blue-200">
        <Settings className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Form Customization:</strong> Click on a section to expand it and manage fields within. 
          Add, remove, or toggle sections and fields as needed.
        </AlertDescription>
      </Alert>

      {/* Form Type Tabs */}
      <Tabs value={activeForm} onValueChange={setActiveForm}>
        <TabsList className="grid grid-cols-5 w-full">
          {FORM_TYPES.map(form => {
            const Icon = form.icon;
            return (
              <TabsTrigger key={form.id} value={form.id} className="text-xs md:text-sm">
                <Icon className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">{form.name}</span>
                <span className="md:hidden">{form.name.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {FORM_TYPES.map(form => (
          <TabsContent key={form.id} value={form.id} className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <form.icon className="w-5 h-5" />
                      {form.name} Configuration
                    </CardTitle>
                    <CardDescription>
                      {sections.filter(s => s.enabled).length} active sections
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {isSuperAdmin && (
                      <Button variant="outline" size="sm" onClick={() => handleResetForm(form.id)}>
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reset
                      </Button>
                    )}
                    <Button size="sm" className="bg-[#1e3a5f]" onClick={() => setShowAddSectionDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Section
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {sections.map((section) => (
                  <Collapsible 
                    key={section.id} 
                    open={expandedSections[section.id]}
                    onOpenChange={() => toggleSectionExpanded(section.id)}
                  >
                    <div className={`border rounded-lg ${section.enabled ? 'border-green-200 bg-green-50' : 'border-slate-200 bg-slate-50 opacity-60'}`}>
                      {/* Section Header */}
                      <CollapsibleTrigger asChild>
                        <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-opacity-80">
                          <div className="flex items-center gap-3">
                            {expandedSections[section.id] ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                            <GripVertical className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-slate-700">{section.title}</span>
                            {section.required && (
                              <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>
                            )}
                            {section.is_custom && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">Custom</Badge>
                            )}
                            <span className="text-xs text-slate-500">
                              ({(section.fields || []).filter(f => f.enabled !== false).length} fields)
                            </span>
                          </div>
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Switch 
                              checked={section.enabled} 
                              onCheckedChange={() => handleToggleSection(form.id, section.id)}
                            />
                            {section.is_custom && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-red-500 hover:text-red-700 h-8 w-8"
                                onClick={() => handleRemoveSection(form.id, section.id, section.title)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CollapsibleTrigger>

                      {/* Section Fields */}
                      <CollapsibleContent>
                        <div className="border-t p-3 space-y-2 bg-white/50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-600">Fields in this section:</span>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => { setSelectedSection(section.id); setShowAddFieldDialog(true); }}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Add Field
                            </Button>
                          </div>
                          
                          {(section.fields || []).length === 0 ? (
                            <p className="text-sm text-slate-500 py-2">No fields in this section</p>
                          ) : (
                            <div className="space-y-1">
                              {(section.fields || []).map((field) => (
                                <div 
                                  key={field.id}
                                  className={`flex items-center justify-between p-2 rounded ${field.enabled !== false ? 'bg-white border border-slate-200' : 'bg-slate-100 border border-slate-200 opacity-50'}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm">{field.label}</span>
                                    <Badge variant="outline" className="text-xs">{field.type}</Badge>
                                    {field.required && (
                                      <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>
                                    )}
                                    {field.is_custom && (
                                      <Badge className="bg-purple-100 text-purple-700 text-xs">Custom</Badge>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Switch 
                                      checked={field.enabled !== false}
                                      onCheckedChange={() => handleToggleField(form.id, section.id, field.id)}
                                      className="scale-75"
                                    />
                                    {field.is_custom && (
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="text-red-500 hover:text-red-700 h-6 w-6"
                                        onClick={() => handleRemoveField(form.id, section.id, field.id, field.label)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}

                {sections.length === 0 && (
                  <p className="text-slate-500 text-center py-8">No sections configured</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Add Section Dialog */}
      <Dialog open={showAddSectionDialog} onOpenChange={setShowAddSectionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#1e3a5f]" />
              Add New Section
            </DialogTitle>
            <DialogDescription>
              Add a section to {FORM_TYPES.find(f => f.id === activeForm)?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2">Section Title *</Label>
              <Input 
                value={newSection.title}
                onChange={(e) => setNewSection(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Additional Observations"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                id="section-required"
                checked={newSection.required}
                onCheckedChange={(c) => setNewSection(prev => ({ ...prev, required: c }))}
              />
              <Label htmlFor="section-required">Required Section</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddSectionDialog(false)}>Cancel</Button>
            <Button onClick={handleAddSection} className="bg-[#1e3a5f]">
              <Plus className="w-4 h-4 mr-2" />
              Add Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Field Dialog */}
      <Dialog open={showAddFieldDialog} onOpenChange={setShowAddFieldDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#1e3a5f]" />
              Add New Field
            </DialogTitle>
            <DialogDescription>
              Add a field to the selected section
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2">Field Label *</Label>
              <Input 
                value={newField.label}
                onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., Additional Notes"
              />
            </div>
            <div>
              <Label className="mb-2">Field Type</Label>
              <Select 
                value={newField.type} 
                onValueChange={(v) => setNewField(prev => ({ ...prev, type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Switch 
                id="field-required"
                checked={newField.required}
                onCheckedChange={(c) => setNewField(prev => ({ ...prev, required: c }))}
              />
              <Label htmlFor="field-required">Required Field</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddFieldDialog(false); setSelectedSection(null); }}>Cancel</Button>
            <Button onClick={handleAddField} className="bg-[#1e3a5f]">
              <Plus className="w-4 h-4 mr-2" />
              Add Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access Management Dialog (Super Admin Only) */}
      <Dialog open={showAccessDialog} onOpenChange={setShowAccessDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1e3a5f]" />
              Form Configuration Access Management
            </DialogTitle>
            <DialogDescription>
              Grant or revoke form configuration access for Lead Therapists and other professionals
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Current Grants */}
            {accessGrants.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <Unlock className="w-4 h-4 text-green-600" />
                  Users with Form Config Access
                </h3>
                <div className="space-y-2">
                  {accessGrants.map(grant => (
                    <div key={grant.id} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded">
                      <div>
                        <span className="font-medium">{grant.user_email}</span>
                        <span className="text-xs text-slate-500 ml-2">
                          Granted: {new Date(grant.granted_at).toLocaleDateString()}
                        </span>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600"
                        onClick={() => handleRevokeAccess(grant.id)}
                      >
                        <Lock className="w-4 h-4 mr-1" />
                        Revoke
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available Users to Grant */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" />
                Grant Access to Staff
              </h3>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {users
                  .filter(u => u.email?.toLowerCase() !== 'admin@icrcl.co.uk')
                  .filter(u => !accessGrants.some(g => g.user_id === u.id))
                  .filter(u => ['clinical_lead', 'educational_lead', 'therapist', 'professional', 'director'].includes(u.role))
                  .map(u => (
                    <div key={u.id} className="flex items-center justify-between p-2 bg-slate-50 border border-slate-200 rounded">
                      <div>
                        <span className="font-medium">{u.first_name} {u.last_name}</span>
                        <span className="text-xs text-slate-500 ml-2">{u.email}</span>
                        <Badge variant="outline" className="ml-2 text-xs">{u.role}</Badge>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-green-600 border-green-600"
                        onClick={() => handleGrantAccess(u.id, u.email)}
                      >
                        <Unlock className="w-4 h-4 mr-1" />
                        Grant
                      </Button>
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAccessDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
